/**
 * NssRadarChart — 8-axis radar showing this uni's NSS 2025 positivity
 * per question theme (Teaching / Learning opportunities / Assessment /
 * Academic support / Organisation / Learning resources / Learning
 * community / Student voice / Overall) plotted against the HESA
 * benchmark. Powered by recharts.
 *
 * Fetches from /api/methodology/nss-2025/provider?uni_id=X.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip,
} from "recharts";

// Map NSS Q-codes to their thematic dimension. Averaging across
// questions within a theme gives us an 8-axis radar rather than a
// 26-axis crowd.
const Q_THEMES = {
  "Teaching":            ["Q01","Q02","Q03","Q04"],
  "Learning opps":       ["Q05","Q06","Q07"],
  "Assessment":          ["Q08","Q09","Q10","Q11"],
  "Academic support":    ["Q12","Q13","Q14"],
  "Organisation":        ["Q15","Q16","Q17"],
  "Learning resources":  ["Q18","Q19","Q20"],
  "Student voice":       ["Q23","Q24","Q25"],
  "Overall satisfaction": ["Q26"],
};

export default function NssRadarChart({ uniId }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!uniId) return;
    api.get(`/methodology/nss-2025/provider?uni_id=${uniId}`)
       .then((r) => setRows(r.data.questions || []))
       .catch(() => setRows([]));
  }, [uniId]);

  const chartData = useMemo(() => {
    if (!rows) return [];
    const byCode = Object.fromEntries(rows.map((r) => [r.question_code, r]));
    return Object.entries(Q_THEMES).map(([theme, codes]) => {
      const found = codes.map((c) => byCode[c]).filter(Boolean);
      if (!found.length) return null;
      const avgPos = found.reduce((a, r) => a + (r.positivity_pct || 0), 0) / found.length;
      const avgBench = found.reduce((a, r) => a + (r.benchmark_pct || 0), 0) / found.length;
      return {
        theme,
        "This uni": Math.round(avgPos * 10) / 10,
        "HESA benchmark": Math.round(avgBench * 10) / 10,
      };
    }).filter(Boolean);
  }, [rows]);

  if (!rows) return null;
  if (chartData.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-stone-300 bg-stone-50 p-4 text-sm text-stone-600" data-testid="nss-radar-empty">
        No HESA NSS 2025 sub-question data available for this university.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="nss-radar-chart">
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[9px] uppercase tracking-wider font-mono">
          HESA verified
        </span>
        <span className="label-uppercase text-emerald-900">
          NSS 2025 · 8-theme student experience map
        </span>
      </div>
      <div style={{ width: "100%", height: 340 }}>
        <ResponsiveContainer>
          <RadarChart data={chartData} outerRadius="72%">
            <PolarGrid stroke="#e7e5e4" />
            <PolarAngleAxis dataKey="theme"
                            tick={{ fontSize: 11, fill: "#4C1D95", fontFamily: "monospace" }} />
            <PolarRadiusAxis angle={90} domain={[50, 100]}
                             tick={{ fontSize: 9, fill: "#a8a29e" }} />
            <Radar name="This uni" dataKey="This uni"
                   stroke="#4C1D95" fill="#4C1D95" fillOpacity={0.35} />
            <Radar name="HESA benchmark" dataKey="HESA benchmark"
                   stroke="#059669" fill="#059669" fillOpacity={0.15} />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-1 text-[11px] text-stone-500 leading-snug">
        Purple = this uni. Green = HESA NSS 2025 country benchmark. Radius shows positivity %; farther out = better. Source · HESA NSS 2025 Q01-Q26.
      </p>
    </div>
  );
}
